/**
 * SceneManager.ts — a tiny scene stack with crossfade transitions.
 *
 * Holds the active scene, drives its update/layout, and animates alpha
 * crossfades when switching. Scenes are created lazily by factory functions.
 */
import { Container, type Application } from 'pixi.js';
import type { Scene } from './Scene';
import type { AppContext, Viewport } from './AppContext';
import { Tweens } from '../util/tween';
import { easeOutCubic } from '../util/ease';
import theme from '../render/theme';

export type SceneFactory = (ctx: AppContext) => Scene;

export class SceneManager {
  readonly root = new Container();
  private current: Scene | null = null;
  private transitioning = false;
  private ctx!: AppContext;

  constructor(app: Application) {
    app.stage.addChild(this.root);
  }

  attachContext(ctx: AppContext): void {
    this.ctx = ctx;
  }

  get viewport(): Viewport {
    return this.ctx.viewport;
  }

  /** Replace the active scene with a crossfade. */
  go(factory: SceneFactory): void {
    if (this.transitioning) return;
    const next = factory(this.ctx);
    next.init(this.ctx);
    next.alpha = 0;
    this.root.addChild(next);
    // enter() builds the scene's contents; it MUST run before layout() so layout()
    // never dereferences not-yet-created members (HUD, dock, canvas, …).
    next.enter();
    next.layout(this.ctx.viewport);

    const prev = this.current;
    this.current = next;

    if (!prev) {
      next.alpha = 1;
      return;
    }
    this.transitioning = true;
    const dur = theme.motion.crossfade;
    Tweens.to({
      from: 0,
      to: 1,
      duration: dur,
      ease: easeOutCubic,
      onUpdate: (v) => {
        next.alpha = v;
        // outgoing scene clears EARLY (≈60% point) so we aren't looking at two half-transparent
        // full layers for the whole transition — that double-translucency is what read "heavy".
        prev.alpha = Math.max(0, 1 - v * 1.7);
      },
      onComplete: () => {
        prev.exit();
        prev.destroy({ children: true });
        this.transitioning = false;
      },
    });
  }

  update(dtMs: number): void {
    this.current?.update(dtMs);
  }

  layout(v: Viewport): void {
    this.current?.layout(v);
  }
}
